import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { timeAxisProps, useQxoFocus } from '@/components/qxo/qxoFocus';
import {
  BoardShell,
  ChartTooltip,
  DataTable,
  KpiGrid,
  LEGEND,
  Panel,
  Section,
  fmtMoneyM,
  fmtNum,
  fmtPct,
  type DataColumn,
  type KpiItem,
} from '@/components/qxo/qxoBoardPrimitives';
import {
  buildDilutionLadder,
  cashAfterFixedCharges,
  computeEnterpriseValue,
  computeFixedCharges,
  type DilutionStep,
} from '@/components/qxo/qxoCapitalMath';
import {
  DEAL_MULTIPLES,
  DEBT_STACK,
  EQUITY_LADDER,
  FILINGS_DESK_AS_OF,
  PLATFORM_BASELINE,
  PREFERRED_STACK,
  PRO_FORMA_FY25,
  SHARE_COUNT,
  type DebtTranche,
  type EquityEvent,
  type PlatformBaseline,
  type PreferredLayer,
} from '@/components/qxo/qxoFilingsData';

/** Used only when the street snapshot has not loaded. */
export const FALLBACK_SPOT = 12.2;

const LADDER_PRICES = Array.from({ length: 23 }, (_, i) => 8 + i); // $8 … $30

const EVENT_COLOR: Record<EquityEvent['kind'], string> = {
  private: '#fbbf24',
  public: '#38bdf8',
  acquisition: '#86efac',
  reference: '#c084fc',
};

const DEBT_COLUMNS: DataColumn<DebtTranche>[] = [
  { key: 'name', header: 'Tranche', render: (r) => r.name },
  { key: 'principal', header: 'Principal', align: 'right', render: (r) => fmtMoneyM(r.principalM) },
  { key: 'rate', header: 'Rate', align: 'right', render: (r) => `${r.ratePct.toFixed(2)}% ${r.rateType === 'floating' ? 'fl.' : 'fx.'}` },
  { key: 'int', header: 'Interest / yr', align: 'right', render: (r) => fmtMoneyM((r.principalM * r.ratePct) / 100) },
  { key: 'mat', header: 'Matures', align: 'right', render: (r) => r.maturity },
  { key: 'sec', header: 'Secured', align: 'right', render: (r) => (r.secured ? 'yes' : 'no') },
];

const PREF_COLUMNS: DataColumn<PreferredLayer>[] = [
  { key: 'name', header: 'Layer', render: (r) => r.name },
  { key: 'holders', header: 'Held by', render: (r) => r.holders },
  { key: 'liq', header: 'Claim', align: 'right', render: (r) => fmtMoneyM(r.liquidationM) },
  { key: 'coupon', header: 'Coupon', align: 'right', render: (r) => fmtPct(r.couponPct, 2) },
  { key: 'div', header: 'Dividend / yr', align: 'right', render: (r) => fmtMoneyM(r.annualDividendM) },
  { key: 'conv', header: 'Conv. price', align: 'right', render: (r) => (r.conversionPrice == null ? '—' : `$${r.conversionPrice.toFixed(2)}`) },
  { key: 'shares', header: 'Shares if converted', align: 'right', render: (r) => `${fmtNum(r.conversionSharesM)}M` },
];

const PLATFORM_COLUMNS: DataColumn<PlatformBaseline>[] = [
  { key: 'p', header: 'Platform', render: (r) => r.platform },
  { key: 'closed', header: 'Closed', render: (r) => r.closed },
  { key: 'price', header: 'Price', align: 'right', render: (r) => `$${r.priceB.toFixed(2)}B` },
  { key: 'sales', header: 'FY25 sales', align: 'right', render: (r) => fmtMoneyM(r.fy25Sales) },
  { key: 'ebitda', header: 'FY25 adj. EBITDA', align: 'right', render: (r) => fmtMoneyM(r.fy25AdjEbitda) },
  { key: 'margin', header: 'Margin', align: 'right', render: (r) => fmtPct(r.fy25MarginPct) },
  { key: 'mult', header: 'Paid ×EBITDA', align: 'right', render: (r) => `${r.paidMultiple.toFixed(1)}x` },
  { key: 't', header: '2030 target', align: 'right', render: (r) => `$${r.target2030EbitdaB.toFixed(1)}B` },
  {
    key: 'need',
    header: 'Needs',
    align: 'right',
    render: (r) => `${((r.target2030EbitdaB * 1000) / r.fy25AdjEbitda).toFixed(1)}x`,
    tone: (r) => ((r.target2030EbitdaB * 1000) / r.fy25AdjEbitda > 2 ? 'text-rose-300' : 'text-amber-300'),
  },
];

const STEP_COLUMNS: DataColumn<DilutionStep>[] = [
  { key: 'label', header: 'Instrument', render: (r) => r.label },
  { key: 'shares', header: 'Adds (M shares)', align: 'right', render: (r) => (r.active ? `+${fmtNum(r.sharesM)}` : '—'), tone: (r) => (r.active ? undefined : 'text-sky-500') },
  { key: 'detail', header: 'Why', render: (r) => r.detail },
];

const EQUITY_COLUMNS: DataColumn<EquityEvent>[] = [
  { key: 'date', header: 'When', render: (r) => r.date },
  { key: 'event', header: 'Event', render: (r) => r.event },
  { key: 'price', header: 'Price', align: 'right', render: (r) => `$${r.price.toFixed(2)}` },
  { key: 'shares', header: 'Shares', align: 'right', render: (r) => `${fmtNum(r.sharesM)}M` },
  { key: 'gross', header: 'Value', align: 'right', render: (r) => fmtMoneyM(r.grossM) },
];

export function QxoCapitalDesk({ spotPrice }: { spotPrice?: number | null }) {
  const spot = spotPrice ?? FALLBACK_SPOT;
  const { focusId } = useQxoFocus();
  const [stressPrice, setStressPrice] = useState<number | null>(null);
  const price = stressPrice ?? spot;

  const charges = useMemo(() => computeFixedCharges(), []);
  const ladder = useMemo(() => buildDilutionLadder(price), [price]);
  const ev = useMemo(() => computeEnterpriseValue(price), [price]);
  const residual = useMemo(() => cashAfterFixedCharges(PRO_FORMA_FY25.combinedAdjEbitda, charges), [charges]);

  const ladderCurve = useMemo(
    () =>
      LADDER_PRICES.map((p) => {
        const l = buildDilutionLadder(p);
        return { price: `$${p}`, fullyDiluted: l.fullyDilutedM, basic: l.basicM + SHARE_COUNT.preFundedWarrantsM };
      }),
    [],
  );

  const waterfall = [
    { step: 'Combined FY25 adj. EBITDA', value: PRO_FORMA_FY25.combinedAdjEbitda, fill: '#38bdf8' },
    { step: 'Cash interest', value: -residual.interestM, fill: '#fb7185' },
    { step: 'Preferred dividends', value: -residual.preferredM, fill: '#f472b6' },
    { step: 'Left for capex, tax, one-offs, common', value: residual.residualM, fill: residual.residualM > 0 ? '#86efac' : '#fb7185' },
  ];

  const dealMultiples = [
    ...DEAL_MULTIPLES.map((d) => ({ name: d.name, multiple: d.evM / d.ebitdaM, fill: '#fbbf24' })),
    { name: `QXO at $${price.toFixed(2)} (pref. as debt)`, multiple: ev.evToEbitdaWithPreferred, fill: '#38bdf8' },
    { name: `QXO at $${price.toFixed(2)} (ex-preferred)`, multiple: ev.evToEbitdaExPreferred, fill: '#1d4ed8' },
  ];

  const weightedIssuePrice =
    EQUITY_LADDER.filter((e) => e.kind !== 'reference').reduce((s, e) => s + e.grossM, 0) /
    EQUITY_LADDER.filter((e) => e.kind !== 'reference').reduce((s, e) => s + e.sharesM, 0);

  const kpis: KpiItem[] = [
    {
      id: 'fixed',
      label: 'Fixed charges / yr',
      value: fmtMoneyM(charges.totalFixedChargesM),
      hint: `${fmtMoneyM(charges.annualInterestM)} interest at ${fmtPct(charges.weightedRatePct, 2)} on ${fmtMoneyM(charges.grossDebtM)} debt, plus ${fmtMoneyM(charges.annualPreferredDividendsM)} preferred dividends.`,
      tone: 'warning',
    },
    {
      id: 'coverage',
      label: 'EBITDA / fixed charges',
      value: `${residual.coverage.toFixed(1)}x`,
      hint: `Combined FY25 adj. EBITDA ${fmtMoneyM(PRO_FORMA_FY25.combinedAdjEbitda)}. ${fmtMoneyM(residual.residualM)} left before capex, cash taxes and integration costs.`,
      tone: residual.coverage < 2.5 ? 'danger' : 'warning',
    },
    {
      id: 'floating',
      label: 'Floating-rate debt',
      value: fmtPct(charges.floatingShareOfDebtPct, 0),
      hint: 'Term loans and ABL. Each 100 bp move in SOFR is ~$39M of annual interest.',
      tone: 'neutral',
    },
    {
      id: 'fd',
      label: `Fully diluted @ $${price.toFixed(2)}`,
      value: `${fmtNum(ladder.fullyDilutedM, 0)}M`,
      hint: `vs ${fmtNum(SHARE_COUNT.commonOutstandingM, 0)}M basic. ${ladder.steps.filter((s) => s.active && s.id.startsWith('Warrants')).length} of 3 warrant tranches in the money; ${fmtMoneyM(ladder.unconvertedPreferredM)} of preferred stays senior.`,
      tone: 'warning',
    },
    {
      id: 'ev',
      label: 'EV / combined FY25 EBITDA',
      value: `${ev.evToEbitdaWithPreferred.toFixed(1)}x`,
      hint: `Counting unconverted preferred as debt. ${ev.evToEbitdaExPreferred.toFixed(1)}x ex-preferred. Beacon was bought at 11.4x, TopBuild at 13.2x.`,
      tone: 'neutral',
    },
    {
      id: 'leverage',
      label: 'Net debt (+pref) / EBITDA',
      value: `${ev.netDebtToEbitda.toFixed(1)}x (${ev.netDebtPlusPreferredToEbitda.toFixed(1)}x)`,
      hint: `Gross debt ${fmtMoneyM(ev.grossDebtM)} less Jun 30 cash ${fmtMoneyM(ev.cashM)}; most of that cash was spent on TopBuild on 1 Jul.`,
      tone: 'danger',
    },
  ];

  return (
    <BoardShell
      id="capital"
      title="QXO"
      subtitle="Capital structure and what common actually owns"
      blurb="Six layers sit between EBITDA and the common share: $9.1B of debt, three preferred classes, and 219M warrants. This board turns the 10-Q notes into a dilution ladder, a fixed-charge bill, and the multiple the market is paying versus what Jacobs paid."
      asOf={FILINGS_DESK_AS_OF}
      sourceLine="10-Q notes 9–11, 8-Ks of 1 & 9 Jul 2026"
      footer="Debt and preferred terms from the Q2 26 10-Q and the 1 Jul 2026 8-K; the incremental term loan rate is an estimate until the Q3 26 10-Q. Combined EBITDA is the sum of the three platforms' FY25 adjusted EBITDA as filed, not a management pro forma. Dilution uses the treasury-stock method for warrants. Math lives in qxoCapitalMath.ts."
    >
      {!focusId || focusId === 'dilution-curve' || focusId === 'deal-multiples' || focusId === 'equity-ladder' ? <div className="flex flex-wrap items-center gap-3 px-5 pb-1 pt-3 text-[12px] text-sky-200">
        <span>Price used for dilution and multiples:</span>
        <span className="font-semibold text-white">${price.toFixed(2)}</span>
        <input
          type="range"
          min={8}
          max={30}
          step={0.5}
          value={price}
          onChange={(e) => setStressPrice(Number(e.target.value))}
          className="w-48 accent-sky-400"
          aria-label="Stress price"
        />
        <button
          type="button"
          onClick={() => setStressPrice(null)}
          className="rounded-md border border-sky-800 px-2 py-0.5 text-[11px] text-sky-300 hover:bg-[#0a2744]"
        >
          reset to spot ${spot.toFixed(2)}
        </button>
      </div> : null}

      <KpiGrid items={kpis} />

      <Section title="Fixed charges">
        Interest and preferred dividends are contractual. At the FY25 combined EBITDA of the three platforms, they
        absorb about {fmtPct(100 / residual.coverage, 0)} of EBITDA before any capex or cash taxes.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="ebitda-waterfall"
          tall
          title="From combined EBITDA to what is left for common"
          unit="$ millions per year, FY25 base"
          takeaway={`${fmtMoneyM(PRO_FORMA_FY25.combinedAdjEbitda)} of EBITDA, ${fmtMoneyM(residual.interestM)} interest, ${fmtMoneyM(residual.preferredM)} preferred. The ${fmtMoneyM(residual.residualM)} left must fund ~$250M capex, cash taxes and integration costs before a dollar reaches common.`}
          note={`QXO's own Article 11 pro forma shows FY25 interest expense of ${fmtMoneyM(-PRO_FORMA_FY25.interestExpense)} and preferred dividends of ${fmtMoneyM(-PRO_FORMA_FY25.preferredDividends)}, GAAP loss to common ${fmtMoneyM(PRO_FORMA_FY25.lossToCommon)} ($${PRO_FORMA_FY25.gaapEps.toFixed(2)}/share) after ${fmtMoneyM(PRO_FORMA_FY25.amortization)} of amortization.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={waterfall} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="step" tick={{ fill: '#93c5fd', fontSize: 9 }} interval={0} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatValue={(v) => fmtMoneyM(v)} />} />
              <ReferenceLine y={0} stroke="#64748b" />
              <Bar dataKey="value" name="$M" radius={[3, 3, 0, 0]}>
                {waterfall.map((row) => (
                  <Cell key={row.step} fill={row.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="debt-stack"
          tall
          fixedHeight={false}
          title="Debt stack after TopBuild"
          unit="$ millions, principal"
          takeaway={`${fmtMoneyM(charges.grossDebtM)} of funded debt at a ${fmtPct(charges.weightedRatePct, 2)} blended rate. Nothing matures before 2030; ${fmtPct(charges.floatingShareOfDebtPct, 0)} floats.`}
          note={DEBT_STACK.filter((d) => d.note).map((d) => `${d.name}: ${d.note}`).join(' ')}
        >
          <DataTable rows={DEBT_STACK} columns={DEBT_COLUMNS} rowKey={(r) => r.name} dense />
        </Panel>
      </div>

      <Section title="Preferred and dilution">
        Three preferred classes total {fmtMoneyM(charges.preferredLiquidationM)} of liquidation preference. Series A
        is deep in the money and behaves like common with a 9% coupon; Series C is a $3.0B senior claim until the
        stock clears $23.25.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="preferred"
          tall
          fixedHeight={false}
          title="Preferred layers"
          unit="$ millions"
          takeaway="Jacobs' Series A converts at $4.566 — 2.7x below spot — so its 219M shares are effectively already common. The public Series B and the Apollo/Temasek Series C are the ones that change the share count with the price."
          note={PREFERRED_STACK.map((p) => `${p.name}: ${p.conversionTerms}`).join(' ')}
        >
          <DataTable rows={PREFERRED_STACK} columns={PREF_COLUMNS} rowKey={(r) => r.name} dense />
        </Panel>

        <Panel datasetId="dilution-curve"
          tall
          title="Fully diluted shares vs price"
          unit="Millions of shares"
          takeaway={`At $${price.toFixed(2)}: ${fmtNum(ladder.fullyDilutedM, 0)}M fully diluted vs ${fmtNum(SHARE_COUNT.commonOutstandingM + SHARE_COUNT.preFundedWarrantsM, 0)}M basic including pre-funded warrants. The step at $23.25 is Series C converting (+129M).`}
          note="Treasury-stock method for warrants (cash strikes offset). Series B follows its filed collar. Options and RSUs excluded."
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ladderCurve} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="price" {...timeAxisProps(focusId != null, 2)} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} domain={[1000, 1600]} />
              <Tooltip content={<ChartTooltip formatValue={(v) => `${fmtNum(v, 0)}M`} />} />
              <ReferenceLine x={`$${Math.round(price)}`} stroke="#fbbf24" strokeDasharray="4 4" />
              <Line type="stepAfter" dataKey="fullyDiluted" name="Fully diluted" stroke="#38bdf8" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="basic" name="Basic + pre-funded" stroke="#64748b" strokeWidth={1.5} dot={false} />
              <Legend wrapperStyle={LEGEND} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4">
        <Panel datasetId="dilution-curve"
          fixedHeight={false}
          title={`Dilution ladder at $${price.toFixed(2)}`}
          unit="Millions of shares added to the basic count"
          takeaway={`Basic ${fmtNum(ladder.basicM, 1)}M → fully diluted ${fmtNum(ladder.fullyDilutedM, 1)}M. Market cap on the diluted count: ${fmtMoneyM(ev.equityM)}.`}
        >
          <DataTable rows={ladder.steps} columns={STEP_COLUMNS} rowKey={(r) => r.id} dense />
        </Panel>
      </div>

      <Section title="Prices paid and prices asked">
        Jacobs has sold or issued roughly 1.0B shares at prices from $9.14 to $23.80; the value-weighted average is
        about ${weightedIssuePrice.toFixed(2)}. The stock is below every price since the 2024 founding round.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="equity-ladder"
          tall
          title="Every price QXO issued stock at"
          unit="$ per share; dashed line is the price in use"
          takeaway="TopBuild holders took 312M shares at an $18.44 reference; the stock was $13 within weeks. Apollo and Temasek's $3.0B converts at $23.25."
          note="Gold = private placements, blue = public offerings, green = shares issued to sellers, purple = conversion reference prices."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={EQUITY_LADDER} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#93c5fd', fontSize: 9 }} interval={0} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${v}`} domain={[0, 26]} />
              <Tooltip content={<ChartTooltip formatValue={(v) => `$${v.toFixed(2)}`} />} />
              <ReferenceLine y={price} stroke="#fbbf24" strokeDasharray="4 4" />
              <Bar dataKey="price" name="Issue price" radius={[3, 3, 0, 0]}>
                {EQUITY_LADDER.map((row) => (
                  <Cell key={`${row.date}-${row.event}`} fill={EVENT_COLOR[row.kind]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          datasetId="equity-ladder"
          overviewOnly
          tall
          fixedHeight={false}
          title="Equity issuance record"
          unit="From the 10-Q equity note and deal 8-Ks"
          takeaway={`About ${fmtMoneyM(EQUITY_LADDER.filter((e) => e.kind !== 'reference').reduce((s, e) => s + e.grossM, 0))} of common issued for cash or assets. Only the June 2024 founding round is under water for QXO's counterparties — everyone since is.`}
        >
          <DataTable rows={EQUITY_LADDER} columns={EQUITY_COLUMNS} rowKey={(r) => `${r.date}-${r.event}`} dense highlightRow={(r) => r.kind === 'reference'} />
        </Panel>
      </div>

      <Section title="What Jacobs paid vs what the market pays him">
        The three platforms were bought at 10.8x–13.2x trailing EBITDA. At the current price QXO trades at{' '}
        {ev.evToEbitdaWithPreferred.toFixed(1)}x the same EBITDA with preferred counted as debt — the market is not
        paying a premium for the roll-up.
      </Section>

      <div className="grid min-w-0 grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-2">
        <Panel datasetId="deal-multiples"
          tall
          title="EV / EBITDA: deals vs the stock"
          unit="× trailing adjusted EBITDA"
          takeaway={`QXO at $${price.toFixed(2)}: ${fmtMoneyM(ev.evWithPreferredM)} EV including ${fmtMoneyM(ev.preferredM)} of unconverted preferred, on ${fmtMoneyM(ev.combinedEbitdaM)} of EBITDA.`}
          note="EV uses the fully diluted share count at the chosen price, gross debt from the stack above, and Jun 30 cash. That cash largely went out the door on 1 Jul for TopBuild, so true net debt is higher — drag the slider, not the cash."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dealMultiples} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
              <CartesianGrid stroke="#1e4976" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} unit="x" domain={[0, 16]} />
              <YAxis type="category" dataKey="name" width={190} tick={{ fill: '#93c5fd', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip formatValue={(v) => `${v.toFixed(1)}x`} />} />
              <Bar dataKey="multiple" name="EV / EBITDA" radius={[0, 3, 3, 0]}>
                {dealMultiples.map((row) => (
                  <Cell key={row.name} fill={row.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel datasetId="platforms"
          tall
          fixedHeight={false}
          title="Three platforms: paid, earned, promised"
          unit="$ millions, FY2025 as filed"
          takeaway="TopBuild is the only platform bought above 12x and the only one earning an 18% margin. The 2030 plan asks Beacon to 2.6x its EBITDA while Kodiak and TopBuild grow ~2x and 1.4x."
          note={PLATFORM_BASELINE.map((p) => `${p.platform}: ${p.source}`).join(' ')}
        >
          <DataTable rows={PLATFORM_BASELINE} columns={PLATFORM_COLUMNS} rowKey={(r) => r.platform} dense />
        </Panel>
      </div>
    </BoardShell>
  );
}
